<template>
    <div class="col-md-12">
        <div class="row">
            <div class="col-md-6">
                <h3 class="page-header" style="margin-bottom: 0px;">
                    <i class="fa fa-mobile animated bounceInDown show-info"></i>
                    <span> App Setting &mdash; Home Banners</span>
                </h3>
            </div>
        </div>

        <!-- single hidden input reused by both sections -->
        <input type="file" ref="fileInput" @change="handleImageUpload" accept="image/*" hidden />

        <!-- ============ TOP CAROUSEL (multiple) ============ -->
        <div class="banner-section">
            <div class="section-head">
                <h4>Top Carousel</h4>
                <small>Auto-playing slider at the top of the app home screen. You can add up to {{ MAX_TOP }} images.</small>
                <div class="size-note">
                    <i class="fa fa-info-circle"></i>
                    Recommended: <b>{{ RECOMMENDED_SIZE }}</b> &bull; JPG / PNG &bull;
                    max <b>{{ MAX_FILE_KB }} KB</b> &bull; up to {{ MAX_DIMENSION }}px per side.
                </div>
            </div>
            <div class="image-container">
                <div v-for="(url, index) in top" :key="'top-' + index" class="image-wrapper">
                    <button class="remove-btn" @click="removeImage('top', index)">&#10006;</button>
                    <img :src="url" class="uploaded-image" />
                </div>
                <div v-if="top.length < MAX_TOP" class="upload-box" @click="openFileDialog('top')">
                    <span>Click to upload</span>
                </div>
            </div>
        </div>

        <!-- ============ MIDDLE BANNER (single) ============ -->
        <div class="banner-section">
            <div class="section-head">
                <h4>Middle Banner</h4>
                <small>Single banner shown further down the app home screen. Uploading replaces the current image.</small>
                <div class="size-note">
                    <i class="fa fa-info-circle"></i>
                    Recommended: <b>{{ RECOMMENDED_SIZE }}</b> &bull; JPG / PNG &bull;
                    max <b>{{ MAX_FILE_KB }} KB</b> &bull; up to {{ MAX_DIMENSION }}px per side.
                </div>
            </div>
            <div class="image-container">
                <div v-for="(url, index) in middle" :key="'middle-' + index" class="image-wrapper">
                    <button class="remove-btn" @click="removeImage('middle', index)">&#10006;</button>
                    <img :src="url" class="uploaded-image" />
                </div>
                <div v-if="middle.length < 1" class="upload-box" @click="openFileDialog('middle')">
                    <span>Click to upload</span>
                </div>
            </div>
        </div>

        <p class="hint">
            Use a <b>2:1</b> (wide) image such as <b>{{ RECOMMENDED_SIZE }}</b> for the best fit.
            Files over <b>{{ MAX_FILE_KB }} KB</b> or larger than <b>{{ MAX_DIMENSION }}px</b> on any side are rejected.
            Changes are saved automatically.
        </p>
    </div>
</template>

<script>
import axios from "axios";
import UploadMixin2 from "../../components/utilities/UploadMixin2.vue";

export default {
    mixins: [UploadMixin2],
    data() {
        return {
            top: [],
            middle: [],
            uploadTarget: "top", // which section the current upload belongs to
            MAX_TOP: 25,
            // Upload constraints (banners render at a 2:1 aspect ratio in the app).
            MAX_FILE_KB: 2048,            // hard limit: 2 MB
            MAX_DIMENSION: 2000,          // reject huge images (px on any side)
            RECOMMENDED_SIZE: "1200 × 600 px",
        };
    },
    mounted() {
        this.fetchConfig();
    },
    methods: {
        fetchConfig() {
            axios.post("api/vsdigi/appsetting/homebanner/fetch")
                .then((response) => {
                    let data = response.data && response.data.data ? response.data.data : {};
                    this.top = Array.isArray(data.top) ? data.top : [];
                    this.middle = Array.isArray(data.middle) ? data.middle : [];
                })
                .catch((error) => console.log(error));
        },
        openFileDialog(target) {
            this.uploadTarget = target;
            this.$refs.fileInput.value = null; // allow re-uploading the same file
            this.$refs.fileInput.click();
        },
        resetInput() {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        },
        handleImageUpload(event) {
            let file = event.target.files && event.target.files[0];
            if (!file) {
                return;
            }

            // 1) type check
            if (file.type !== "image/jpeg" && file.type !== "image/png") {
                alert("Only JPG or PNG images are allowed.");
                this.resetInput();
                return;
            }

            // 2) file-size check (no huge uploads)
            let kb = Math.round(file.size / 1024);
            if (kb > this.MAX_FILE_KB) {
                alert("This image is " + kb + " KB. Please upload an image under " +
                    this.MAX_FILE_KB + " KB (2 MB).");
                this.resetInput();
                return;
            }

            // 3) dimension check, then upload
            let self = this;
            let objectUrl = (window.URL || window.webkitURL).createObjectURL(file);
            let img = new Image();
            img.onload = function () {
                (window.URL || window.webkitURL).revokeObjectURL(objectUrl);
                if (this.width > self.MAX_DIMENSION || this.height > self.MAX_DIMENSION) {
                    alert("This image is " + this.width + "×" + this.height +
                        " px. Maximum allowed is " + self.MAX_DIMENSION + " px on any side.");
                    self.resetInput();
                    return;
                }
                self.doUpload(event);
            };
            img.onerror = function () {
                (window.URL || window.webkitURL).revokeObjectURL(objectUrl);
                alert("Could not read this image. Please try another file.");
                self.resetInput();
            };
            img.src = objectUrl;
        },
        doUpload(event) {
            // Reuse the existing "amazon controller" presigned upload flow.
            // width/height = 1000 => any dimensions accepted; the <=2MB size is
            // already enforced above (and again inside the mixin as a safety net).
            let obj = {
                type: 0, directorypath: "appbanner", counter: 0,
                typeid: 0, typeint: 0, width: 1000, height: 1000,
            };
            this.$store.commit("assignfilecomponents", obj);
            this.$store.commit("assignfieldjson", {});
            this.processFile2(event);
        },
        // called by UploadMixin2 after the image is saved to amazon
        uploaded() {
            let url = this.uploadedimage && this.uploadedimage.fullurl ? this.uploadedimage.fullurl : null;
            if (!url) {
                return;
            }
            if (this.uploadTarget === "top") {
                if (this.top.length >= this.MAX_TOP) {
                    alert("You can add up to " + this.MAX_TOP + " top banners.");
                    return;
                }
                if (!this.top.includes(url)) {
                    this.top.push(url);
                }
            } else {
                this.middle = [url]; // single image, replaces existing
            }
            this.persist();
        },
        removeImage(target, index) {
            if (!confirm("Are you sure you want to remove this banner?")) {
                return;
            }
            if (target === "top") {
                this.top.splice(index, 1);
            } else {
                this.middle.splice(index, 1);
            }
            this.persist();
        },
        persist() {
            axios.post("api/vsdigi/appsetting/homebanner/update", { top: this.top, middle: this.middle })
                .then((response) => {
                    if (response.data && response.data.data) {
                        this.top = response.data.data.top || [];
                        this.middle = response.data.data.middle || [];
                    }
                    this.$notify ? this.$notify({ text: "Saved", type: "success" }) : null;
                })
                .catch((error) => console.log(error));
        },
    },
};
</script>

<style scoped>
.banner-section {
    margin-top: 25px;
    padding: 15px;
    border: 1px solid #e3e8ef;
    border-radius: 6px;
}
.section-head h4 {
    margin: 0 0 3px 0;
}
.section-head small {
    color: #8a93a2;
}
.size-note {
    margin-top: 6px;
    font-size: 12px;
    color: #495b79;
    background: #eef2f8;
    border: 1px solid #dbe3ef;
    border-radius: 4px;
    padding: 6px 10px;
    display: inline-block;
}
.size-note i {
    margin-right: 5px;
}
.image-container {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 15px;
}
.image-wrapper {
    position: relative;
    display: inline-block;
}
.uploaded-image {
    width: 220px;
    height: 120px;
    object-fit: cover;
    border-radius: 5px;
    border: 1px solid #e3e8ef;
}
.upload-box {
    width: 220px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed #ccc;
    cursor: pointer;
    text-align: center;
    border-radius: 5px;
    color: #8a93a2;
}
.remove-btn {
    position: absolute;
    top: -6px;
    right: -6px;
    background: red;
    color: white;
    border: none;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    cursor: pointer;
    font-size: 12px;
    z-index: 2;
}
.hint {
    margin-top: 18px;
    color: #8a93a2;
}
</style>
